import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import SectionHeading from '@/components/base/SectionHeading';
import MediaCard from '@/components/feature/MediaCard';
import { mediaList } from '@/mocks/media';
import { categoryMeta } from '@/mocks/filters';
import { relatedMedia } from '@/utils/media';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useFavorites } from '@/hooks/useFavorites';
import { MEDIA_TYPE_LABEL } from '@/types/media';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = mediaList.find((m) => m.id === id);

  const [playerOpen, setPlayerOpen] = useState(false);
  const [toast, setToast] = useState('');
  const { isFavorite, toggleFavorite } = useFavorites();

  useDocumentTitle(
    item ? `${item.title} - 蘑菇视频` : '内容未找到 - 蘑菇视频',
    item ? item.synopsis.slice(0, 110) : '未找到对应的影视内容。',
  );

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!playerOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPlayerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [playerOpen]);

  if (!item) {
    return (
      <div className="w-full max-w-content mx-auto px-4 md:px-8 py-16">
        <EmptyState
          title="未找到该影视内容"
          description="该内容可能已下架或链接有误，返回首页浏览更多精彩影视。"
          icon="ri-film-line"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const meta = categoryMeta[item.type];
  const favorited = isFavorite(item.id);
  const related = relatedMedia(mediaList, item, 8);

  const handleFavorite = () => {
    const now = toggleFavorite(item.id);
    setToast(now ? '已加入收藏' : '已取消收藏');
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast('链接已复制到剪贴板');
    } catch {
      setToast('复制失败，请手动复制地址栏链接');
    }
  };

  const infoRows = [
    { label: '导演', value: item.director },
    { label: '主演', value: item.cast.join(' / ') },
    { label: '类型', value: item.genres.join(' · ') },
    { label: '地区', value: item.region },
    { label: '上映', value: `${item.year} 年` },
    { label: '片长', value: item.episodes },
  ];

  return (
    <div className="w-full max-w-content mx-auto px-4 md:px-8 py-6">
      <Breadcrumb items={[{ label: meta.name, to: `/category/${item.type}` }, { label: item.title }]} />

      <section className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        <div className="lg:col-span-4 xl:col-span-3">
          <div className="w-full max-w-[260px] lg:max-w-none mx-auto rounded-lg overflow-hidden bg-background-200 border border-background-200">
            <img
              src={item.poster}
              alt={`${item.title} 影视海报`}
              title={`${item.title} - 蘑菇视频`}
              className="w-full aspect-[2/3] object-cover object-top"
            />
          </div>
        </div>

        <div className="lg:col-span-8 xl:col-span-9 flex flex-col">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap">
              {MEDIA_TYPE_LABEL[item.type]}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs font-medium whitespace-nowrap">
              {item.region}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-800 text-xs font-medium whitespace-nowrap">
              {item.year}
            </span>
          </div>

          <h1 className="text-2xl md:text-4xl font-black text-foreground-950 mb-2">{item.title}</h1>
          <p className="text-sm md:text-base text-primary-600 font-medium mb-3">{item.tagline}</p>

          <div className="flex items-center gap-4 mb-5">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-star-fill text-primary-500 text-lg"></i>
              <span className="font-heading font-black text-xl text-foreground-950">{item.rating.toFixed(1)}</span>
              <span className="text-xs text-foreground-400">分</span>
            </span>
            <span className="inline-flex items-center gap-1 text-sm text-foreground-500">
              <i className="ri-fire-line text-primary-400"></i>
              热度 {item.popularity}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <button
              type="button"
              onClick={() => setPlayerOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-fill text-xl"></i>
              立即播放
            </button>
            <button
              type="button"
              onClick={handleFavorite}
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                favorited
                  ? 'bg-primary-100 text-primary-700 border border-primary-300'
                  : 'bg-background-100 text-foreground-700 border border-background-200 hover:border-primary-300'
              }`}
            >
              <i className={favorited ? 'ri-heart-3-fill text-lg' : 'ri-heart-3-line text-lg'}></i>
              {favorited ? '已收藏' : '收藏'}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-background-100 text-foreground-700 border border-background-200 text-sm font-medium hover:border-primary-300 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-share-forward-line text-lg"></i>
              分享
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 mb-6">
            {infoRows.map((row) => (
              <div key={row.label} className="flex items-start gap-3 text-sm">
                <span className="shrink-0 text-foreground-400 w-10">{row.label}</span>
                <span className="text-foreground-800">{row.value}</span>
              </div>
            ))}
          </div>

          <div className="rounded-lg bg-background-100 border border-background-200 p-4 md:p-5">
            <h2 className="text-sm font-bold text-foreground-900 mb-2">剧情简介</h2>
            <p className="text-sm leading-relaxed text-foreground-600">{item.synopsis}</p>
          </div>

          <div className="mt-5 flex items-center gap-2 flex-wrap">
            <span className="text-sm text-foreground-500">标签：</span>
            {item.genres.map((genre) => (
              <Link
                key={genre}
                to={`/category/${item.type}/filter`}
                className="px-3 py-1.5 rounded-md bg-secondary-100 text-secondary-900 text-xs font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                {genre}
              </Link>
            ))}
            <Link
              to={`/category/${item.type}`}
              className="px-3 py-1.5 rounded-md bg-accent-100 text-accent-800 text-xs font-medium hover:bg-accent-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              更多{item.type === 'movie' ? '电影' : item.type === 'tv' ? '电视剧' : '综艺'}
            </Link>
          </div>
        </div>
      </section>

      <section className="mt-12">
        <SectionHeading title="相关推荐" subtitle="根据类型与地区为你精选的相似内容" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
          {related.map((r) => (
            <MediaCard key={r.id} item={r} />
          ))}
        </div>
      </section>

      {playerOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-secondary-950/80 p-4"
          onClick={() => setPlayerOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="视频播放演示"
        >
          <div
            className="w-full max-w-3xl rounded-lg overflow-hidden bg-secondary-950 border border-background-700/40"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 bg-secondary-900">
              <span className="text-sm font-medium text-background-100 truncate">{item.title} · 演示播放</span>
              <button
                type="button"
                onClick={() => setPlayerOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-background-200 hover:bg-background-50/10 transition-colors cursor-pointer"
                aria-label="关闭播放器"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="relative w-full aspect-video bg-secondary-950 flex flex-col items-center justify-center text-center px-6">
              <div className="w-16 h-16 rounded-full bg-primary-500/90 flex items-center justify-center mb-4">
                <i className="ri-play-fill text-3xl text-background-50"></i>
              </div>
              <p className="text-background-100 font-medium mb-1">演示视频播放区域</p>
              <p className="text-xs text-background-400 max-w-md">
                当前为前端演示页面，未接入真实视频播放接口，正式上线后将在此处播放《{item.title}》正片内容。
              </p>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-md bg-secondary-950 text-background-50 text-sm shadow-lg animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}